import * as _ from 'underscore';
import { MetadataClient } from 'mappClient/metadataClient';
import { AppClient } from 'mappClient/clients';
import { AppType } from 'mappClient/enums';
import { ListView } from 'mappMvc/models/views';
import { IMApp } from 'mappClient/models';
import { SelectClickableCollection, SelectClickable } from 'mappMvc/models/selection';
import {
	ViewOptions,
	ColumnOptions,
	ListViewOptions,
	StudioNotificationOptions
} from 'mappMvc/interfaces/options';
import { Localization } from 'mappMvc/compile';
import { MApp } from 'mappMvcStudio/app/mapp';
import { Constants } from 'mappMvcStudio/app';
import { ID, Icons } from '../../../const';
import { Templates } from '../../../compile';
import { SelectionMode, NotificationType, FilterOperator, PropertyType, StringComparer } from 'mappMvc/enums';
import { NavigationItem } from 'mappMvc/models/navigation';
import { DefaultBaseTableCommandColumnOptions } from 'mappMvc/const/defaultOptions/index';
import { MAppSection } from '.';
import { StudioNotificationFactory, Util } from 'mappMvc/models';

export class MAppList extends ListView<IMApp> {
	private templateSwitch: SelectClickableCollection<SelectClickable>;
	private clickLaunch: (mapp: IMApp) => void;
	private columnWidth: KnockoutObservable<string>;
	private calculateColumnWidth: () => void;

	public browserFilterChecked: KnockoutObservable<boolean>;
	public desktopFilterChecked: KnockoutObservable<boolean>;
	public mobileFilterChecked: KnockoutObservable<boolean>;

	public clickBrowserFilter: () => void;
	public clickDesktopFilter: () => void;
	public clickMobileFilter: () => void;

	constructor(options: ViewOptions) {
		var commandOptions: ColumnOptions<IMApp> = _.extend(
			{},
			DefaultBaseTableCommandColumnOptions<IMApp>(),
			<ColumnOptions<IMApp>>{
				key: ID.Commands,
				commands: [
					{
						label: Localization.Launch,
						css: Icons.Launch,
						command: (mapp: IMApp) => this.clickLaunch(mapp)
					}
				]
			}
		);

		var listViewOptions: ListViewOptions<IMApp> = {
			view: {
				viewId: ID.MApps,
				template: Templates.mapps_tileGrid,
				label: Localization.License.MApps,
				icon: Icons.MApps
			},
			table: {
				selectionMode: SelectionMode.NONE,
				dataSource: {
					get: () => this.loadMApps()
				},
				textFilterColumn: ID.Name,
				properties: [{ key: ID.Name, label: Localization.Name }, commandOptions]
			}
		};

		_.extend(listViewOptions.view, options);
		super(listViewOptions);

		this.browserFilterChecked = ko.observable<boolean>(true);
		this.desktopFilterChecked = ko.observable<boolean>(true);
		this.mobileFilterChecked = ko.observable<boolean>(true);

		this.clickBrowserFilter = () => this.browserFilterChecked(!this.browserFilterChecked());
		this.clickDesktopFilter = () => this.desktopFilterChecked(!this.desktopFilterChecked());
		this.clickMobileFilter = () => this.mobileFilterChecked(!this.mobileFilterChecked());

		this.browserFilterChecked.subscribe(() => this.setTypeFilter());
		this.desktopFilterChecked.subscribe(() => this.setTypeFilter());
		this.mobileFilterChecked.subscribe(() => this.setTypeFilter());
		this.table.searchInputSubscription.dispose();
		this.table.searchInput.subscribe(() => this.setTypeFilter());

		var navigationItems: NavigationItem[] = [];
		navigationItems.push(
			new NavigationItem({
				id: ID.MAppsTable,
				label: '',
				icon: Icons.MAppsTable,
				callBack: () => {
					this.template(Templates.mapps_table);
				}
			})
		);

		navigationItems.push(
			new NavigationItem({
				id: ID.MAppsTileGrid,
				isSelected: true,
				label: '',
				icon: Icons.MAppsTileGrid,
				callBack: () => {
					this.template(Templates.mapps_tileGrid);
				}
			})
		);

		this.templateSwitch = new SelectClickableCollection({
			items: navigationItems,
			template: Templates.header_horizontalSwitch
		});

		this.clickLaunch = (mapp: IMApp) => {
			if (mapp.appType === AppType.Desktop) {
				let token = MetadataClient.getToken();
				this.showHexagonAppLauncherNotification();
				var link = document.createElement('a');
				link.hostname = window.location.hostname;
				link.host = window.location.host;
				link.pathname = window.location.pathname;
				link.href = `../api/v1/desktopclient/${mapp.id}.hnlp?tenant=${MetadataClient.tenant
					}&refreshToken=${token.refresh_token}&accessToken=${token.access_token}`;
				link.protocol = window.location.protocol === 'https:' ? 'hnlps' : 'hnlp';
				window.location.replace(link.href);
			} else if (mapp.appType === AppType.Browser) {
				let url = mapp.isLegacyBrowserMapp
					? `../api/v1/browserclient/${mapp.id}?tenant=${MetadataClient.tenant}`
					: `../AppEngine?tenant=${MetadataClient.tenant}&appId=${mapp.id}`;
				window.open(url);
			} else if (mapp.appType === AppType.Mobile) {
				(<MAppSection>this.parent).edit(mapp);
			}
		};

		this.columnWidth = ko.observable<string>();

		this.calculateColumnWidth = () => {
			let sectionSelector = '.mapp-mapps-section-wrapper';
			let width = $(sectionSelector).width();

			this.columnWidth(100 / Math.ceil(width / Constants.AppTileWidth) + '%');
			this.table.dataSource.pagingDataMap.pageSize(
				Math.max(Math.ceil(width / Constants.AppTileWidth) * 3, 6)
			);
		};

		$(window).resize(this.calculateColumnWidth);
	}

	public loadMApps(): JQueryPromise<IMApp[]> {
		let deferred = $.Deferred<IMApp[]>();

		AppClient.getMApps().then(mapps => {
			deferred.resolve(mapps.map((mapp: IMApp) => {
				// TODO: where are the roles (not roleIds) coming from?
				return new MApp(mapp.id, mapp.name, mapp.image, mapp.appType, mapp.roleIds, undefined, mapp.isLegacyBrowserMapp);
			})
			)
				.then(this.calculateColumnWidth);
		});

		return deferred.promise();
	}

	private setTypeFilter() {
		let showDesktop = this.desktopFilterChecked();
		let showBrowser = this.browserFilterChecked();
		let showMobile = this.mobileFilterChecked();

		let blockList = [];
		if (!showDesktop)
			blockList.push(AppType.Desktop);
		if (!showBrowser)
			blockList.push(AppType.Browser);
		if (!showMobile)
			blockList.push(AppType.Mobile);

		let textFilter = {
			key: ID.Name,
			operator: FilterOperator.LIKE,
			params: [this.table.searchInput()],
			type: PropertyType.STRING,
			stringComparer: StringComparer.CaseInSensitive
		}

		this.table.dataSource.filter([textFilter, {
			key: 'appType',
			operator: FilterOperator.ISNOTIN,
			params: [blockList],
			type: PropertyType.NUMBER
		}]);
	}

	public new() {
		return null;
	}

	public edit(mapp: IMApp) {
		return null;
	}

	public delete(mapp: IMApp): JQueryPromise<any> {
		return null;
	}

	private showHexagonAppLauncherNotification() {
		const cookieEntry = 'al_hint_displayed=true';

		let setCookie = (due?: Date) => {
			document.cookie = `${cookieEntry}; expires = ${due ? due.toUTCString() : 'Fri, 31 Dec 9999 23:59:59 GMT'
				}`;
		};
		let loadingAnimationDuration = 3500;

		if (document.cookie.split(';').indexOf(cookieEntry) < 0) {
			loadingAnimationDuration *= 2;
			window.setTimeout(() => {
				Util.StudioNotification.show(<StudioNotificationOptions>{
					template: Templates.mapps_launchNotification,
					okClick: () => {
						setCookie();
						return true;
					},
					closeClick: () => {
						let d = new Date();
						setCookie(new Date(d.setDate(d.getDate() + 1)));
					},
					duration: 5
				});
			}, 2000);
		}

		//#region fake loading animation - I dont like it, I would implement a handshake-based system
		Util.loading(true, '.mapp-content');
		window.setTimeout(() => {
			Util.loadingOff();
		}, loadingAnimationDuration);
		//#endregion
	}
}
